import { useLingui, Trans } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, toast } from '@utima/ui';
import { Input, Checkbox, type TypedFormState } from '@utima/ui-informed';
import { jwtDecode, type JwtPayload } from 'jwt-decode';
import { z } from 'zod';

import { PasswordFields } from '@/components/fields/passwordFields/PasswordFields';
import { BasicForm } from '@/components/form/BasicForm';
import { trpc } from '@/services/trpc';

export const Route = createFileRoute('/_authLayout/verify/')({
  component: VerificationPage,
  validateSearch: (search: Record<string, unknown>): { token: string } => {
    return {
      token: (search.token as string) || '',
    };
  },
});

export function VerificationPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const verifyMutation = trpc.auth.verify.useMutation();

  const { token } = Route.useSearch();

  let expiresAt: number = 0;
  const initialValues = {
    email: '',
    password: '',
    confirmPassword: '',
    consentWithToC: false,
  };

  try {
    const jwtPayload = jwtDecode(token) as JwtPayload & { email: string };
    initialValues.email = jwtPayload.email;
    expiresAt = jwtPayload.exp!;
  } catch (e) {
    console.error(e);
  }

  if (!token || !initialValues.email || expiresAt < Date.now() / 1000) {
    return (
      <div className='flex flex-col items-center gap-6'>
        <h1 className='text-center text-2xl font-bold'>
          <Trans>Verification</Trans>
        </h1>
        <div className='text-center text-sm'>
          <Trans>Invalid or expired verification token</Trans>
        </div>
      </div>
    );
  }

  const onSubmit = (formState: TypedFormState<typeof initialValues>) => {
    verifyMutation.mutate(
      {
        token: token,
        email: formState.values.email,
        password: formState.values.password,
      },
      {
        onError: (error: unknown) => {
          console.error(error);
          toast.error(t`Verification Error`, {
            description: t`There was an error verifying your account. Please try again.`,
          });
        },
        onSuccess: data => {
          toast.success(t`Verification Successful`, {
            description: t`Your account has been verified successfully. You can now log in.`,
          });

          navigate({ to: '/login' });
        },
      },
    );
  };

  return (
    <BasicForm
      onSubmit={onSubmit}
      className='flex w-full flex-col gap-6'
      zodSchema={z.object({
        email: z.string().email(),
        password: z.string().min(6),
        confirmPassword: z.string().min(6),
        consentWithToC: z.boolean().refine(val => val === true, {
          message: t`You must agree to the Terms and Conditions`,
        }),
      })}
      initialValues={initialValues}
    >
      <h1 className='text-center text-2xl font-bold'>
        <Trans>Verification</Trans>
      </h1>
      <Input
        name='email'
        type='email'
        data-cta='admin-verify-email-input'
        disabled
        required
      />
      <PasswordFields title={t`Set Your Password`} hasOldPassword={false} />
      {/* Terms & Conditions checkbox */}
      <div className='flex items-center justify-start'>
        <div className='mr-2 w-full'>
          <Checkbox
            size='sm'
            name='consentWithToC'
            // @ts-expect-error the react component is supported but not typed properly
            description={
              <>
                <Trans>I agree to the</Trans>
                <a
                  href={t`https://cdn.prod.website-files.com/67a1d99a62c51e3f81d0dee0/67d429f06ca9487ea95ddf30_Kanbu.cz_Zpracovatelske%CC%81%20podmi%CC%81nky_EN.pdf`}
                  target='_blank'
                  rel='noreferrer'
                  className='ml-1 underline'
                >
                  <Trans>Terms and Conditions</Trans>
                </a>
              </>
            }
            required
          />
        </div>
      </div>
      <Button
        data-cta='admin-verify-submit-button'
        variant='primary'
        type='submit'
      >
        <Trans>Register</Trans>
      </Button>
    </BasicForm>
  );
}
